import React, { useEffect, useMemo, useRef, useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faGripVertical,
  faPenToSquare,
  faTrash,
  faUpload,
  faXmark,
} from "@fortawesome/free-solid-svg-icons";
import { toast } from "react-toastify";
import Loader from "../../../../common/Loader";
import {
  AffinityProgramPartnerActivationStatusEnum,
  AffinityProgramPartnerFieldsFragment,
  AffinityProgramPartnerInput,
  useCreateAffinityProgramPartnerMutation,
  useDeleteAffinityProgramPartnerMutation,
  useGetAllAffinityProgramPartnersForChamberQuery,
  useUpdateAffinityProgramPartnerMutation,
} from "../../../../../graphql/types";
import { CalendarDateInput } from "../common";

const maxDescriptionLength = 2000;
const maxLogoSizeBytes = 5 * 1024 * 1024;

type AffinityProgramDraft = {
  companyName: string;
  description: string;
  logo: string;
  startDate: string;
  title: string;
  redirectLink: string;
};

const createTodayDateInputValue = () => {
  const date = new Date();
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");

  return `${year}-${month}-${day}`;
};

const createBlankDraft = (): AffinityProgramDraft => ({
  companyName: "",
  description: "",
  logo: "",
  startDate: createTodayDateInputValue(),
  title: "",
  redirectLink: "",
});

const createPartnerId = () =>
  `affinity-program-partner-${Date.now()}-${Math.random()
    .toString(36)
    .slice(2, 10)}`;

const fileToDataUrl = (file: File): Promise<string> =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result || ""));
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });

const dateInputValueToUtcMilli = (value: string) => {
  const [year, month, day] = value.split("-").map(Number);

  if (!year || !month || !day) {
    return null;
  }

  return new Date(year, month - 1, day, 12, 0, 0, 0).getTime();
};

const utcMilliToDateInputValue = (utcMilli?: number | null) => {
  if (!utcMilli) {
    return createTodayDateInputValue();
  }

  const date = new Date(utcMilli);
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");

  return `${year}-${month}-${day}`;
};

const formatDate = (utcMilli?: number | null) => {
  if (!utcMilli) {
    return "";
  }

  return new Intl.DateTimeFormat("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  }).format(new Date(utcMilli));
};

const formatSavedAt = (utcMilli?: number | null) => {
  if (!utcMilli) {
    return "";
  }

  return `Last Saved ${new Intl.DateTimeFormat("en-US", {
    dateStyle: "long",
    timeStyle: "short",
  }).format(new Date(utcMilli))}`;
};

const truncateDescription = (description: string) => {
  if (description.length <= 128) {
    return description;
  }

  return `${description.slice(0, 125).trimEnd()}...`;
};

const isPublishedPartner = (
  partner: Pick<AffinityProgramPartnerFieldsFragment, "activationStatus">
) => partner.activationStatus !== AffinityProgramPartnerActivationStatusEnum.Deactivated;

const buildAffinityProgramInput = ({
  id,
  draft,
}: {
  id: string;
  draft: AffinityProgramDraft;
}): AffinityProgramPartnerInput => ({
  id,
  companyName: draft.companyName.trim(),
  title: draft.companyName.trim(),
  description: draft.description.trim(),
  redirectLink: draft.redirectLink,
  logo: draft.logo,
  banner: [],
});

const validateDraft = (draft: AffinityProgramDraft) => {
  if (!draft.companyName.trim()) {
    return "Affinity Partner Name is required.";
  }

  if (!draft.description.trim()) {
    return "Affinity Partner Description is required.";
  }

  if (draft.description.length > maxDescriptionLength) {
    return "Affinity Partner Description must be 2000 characters or fewer.";
  }

  if (!draft.logo) {
    return "Affinity Partner Logo is required.";
  }

  if (!dateInputValueToUtcMilli(draft.startDate)) {
    return "Start Date is required.";
  }

  return null;
};

const LogoUploadField = ({
  value,
  onChange,
  onError,
}: {
  value: string;
  onChange: (value: string) => void;
  onError: (message: string) => void;
}) => {
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  const handleOpenFilePicker = () => {
    fileInputRef.current?.click();
  };

  const handleFileChange = async (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const file = event.target.files?.[0];
    event.target.value = "";

    if (!file) {
      return;
    }

    if (!["image/png", "image/jpeg"].includes(file.type)) {
      onError("Use a PNG or JPG Affinity Partner Logo image.");
      return;
    }

    if (file.size > maxLogoSizeBytes) {
      onError("Affinity Partner Logo image must be 5MB or smaller.");
      return;
    }

    try {
      onChange(await fileToDataUrl(file));
    } catch (error) {
      onError("Could not read the Affinity Partner Logo image.");
    }
  };

  return (
    <div className="w-full max-w-[430px]">
      {/* Per the Chamber Setup screenshots (CS-011), the Update Logo and
          Delete Logo actions live INSIDE the dashed upload container. */}
      {value ? (
        <div className="flex min-h-[285px] w-full flex-col items-center justify-center rounded-lg border-2 border-dashed border-gray-700 bg-white p-5">
          <div className="flex h-[150px] w-[150px] items-center justify-center rounded-lg border border-gray-500 bg-white p-3">
            <img
              src={value}
              alt="Affinity Partner Logo preview"
              className="max-h-full max-w-full object-contain"
            />
          </div>
          <button
            type="button"
            onClick={handleOpenFilePicker}
            className="mt-4 inline-flex h-10 items-center justify-center gap-2 rounded-lg border border-gray-600 bg-gray-600 px-5 text-base font-bold text-white transition hover:bg-gray-700"
          >
            <FontAwesomeIcon icon={faUpload} />
            Update Logo
          </button>
          <button
            type="button"
            onClick={() => onChange("")}
            className="mt-3 inline-flex h-10 items-center justify-center gap-2 rounded-lg border border-red-500 bg-red-500 px-5 text-base font-bold text-white transition hover:bg-red-600"
          >
            <FontAwesomeIcon icon={faTrash} />
            Delete Logo
          </button>
        </div>
      ) : (
        <button
          type="button"
          onClick={handleOpenFilePicker}
          className="flex min-h-[285px] w-full flex-col items-center justify-center rounded-lg border-2 border-dashed border-gray-700 bg-white p-5 transition hover:border-cpaGold hover:bg-amber-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-cpaGold/40"
        >
          <div className="flex flex-col items-center text-gray-500">
            <FontAwesomeIcon icon={faUpload} className="text-3xl" />
            <span className="mt-6 text-2xl font-normal">Click Upload Logo</span>
          </div>
        </button>
      )}
      <input
        ref={fileInputRef}
        type="file"
        accept="image/png,image/jpeg"
        className="hidden"
        onChange={handleFileChange}
      />
    </div>
  );
};

const AffinityProgramForm = ({
  draft,
  onChange,
  onSubmit,
  onReset,
  submitting,
  error,
  variant,
  lastSavedLabel,
}: {
  draft: AffinityProgramDraft;
  onChange: (draft: AffinityProgramDraft) => void;
  onSubmit: () => void | Promise<void>;
  onReset?: () => void;
  submitting: boolean;
  error: string | null;
  variant: "create" | "edit";
  lastSavedLabel?: string;
}) => {
  const helperText =
    variant === "create"
      ? "Enter In Affinity Program Details Below And Click Save."
      : "Edit the Affinity Program details below and click Save.";

  return (
    <div>
      <div className="bg-gray-50 px-5 py-4">
        <p className="text-lg font-bold text-gray-950">{helperText}</p>
      </div>

      <div className="mt-6 grid grid-cols-1 gap-7 lg:grid-cols-[430px_minmax(0,1fr)]">
        <div className="mx-auto w-full max-w-[430px] lg:mx-0">
          <p className="text-lg font-bold text-gray-950">
            Affinity Partner Logo
          </p>
          <p className="mt-2 text-sm text-gray-900">
            Ideally a square&nbsp; PNG or JPG image 5MB in size
          </p>
          <div className="mt-3">
            <LogoUploadField
              value={draft.logo}
              onChange={(logo) => onChange({ ...draft, logo })}
              onError={(message) => {
                toast.error(message);
              }}
            />
          </div>
        </div>

        <div className="space-y-4">
          <input
            value={draft.companyName}
            onChange={(event) =>
              onChange({ ...draft, companyName: event.target.value })
            }
            placeholder="Affinity Partner Name As Also Seen On Website"
            className="h-12 w-full rounded-lg border border-gray-500 bg-white px-4 text-base text-gray-950 outline-none transition placeholder:text-gray-500 focus:border-cpaGold focus:ring-2 focus:ring-amber-100 disabled:cursor-not-allowed disabled:bg-gray-100"
            disabled={submitting}
          />
          <textarea
            value={draft.description}
            onChange={(event) =>
              onChange({
                ...draft,
                description: event.target.value.slice(0, maxDescriptionLength),
              })
            }
            placeholder="Affinity Partner Description As Also Seen On Website"
            className="min-h-[260px] w-full resize-y rounded-lg border border-gray-500 bg-white px-4 py-3 text-base leading-7 text-gray-950 outline-none transition placeholder:text-gray-500 focus:border-cpaGold focus:ring-2 focus:ring-amber-100 disabled:cursor-not-allowed disabled:bg-gray-100"
            disabled={submitting}
          />
          <p className="text-base text-gray-500">
            {draft.description.length}/{maxDescriptionLength} characters
          </p>
        </div>
      </div>

      {variant === "edit" && (
        <div className="mt-8">
          <label className="block">
            <span className="block text-base font-bold text-gray-950">
              Start Date
            </span>
            <span className="mt-2 block text-sm text-slate-600">
              Set up the start date for the Affinity Program Partner
            </span>
            <CalendarDateInput
              mode="fullDate"
              ariaLabel="Affinity Program Partner start date"
              value={draft.startDate}
              onChange={(startDate) =>
                onChange({ ...draft, startDate })
              }
              disabled={submitting}
              className="mt-4"
              inputClassName="h-14 w-full rounded-lg border border-gray-200 bg-white px-4 text-base text-gray-950 shadow-sm outline-none transition focus:border-cpaGold focus:ring-2 focus:ring-amber-100 disabled:cursor-not-allowed disabled:bg-gray-100"
            />
          </label>
        </div>
      )}

      {error && (
        <p className="mt-5 rounded-lg bg-red-50 px-4 py-3 text-sm font-semibold text-red-700">
          {error}
        </p>
      )}

      <div className="mt-8 flex flex-col items-stretch gap-3 sm:flex-row sm:items-start sm:justify-end">
        {variant === "edit" && onReset && (
          <button
            type="button"
            onClick={onReset}
            disabled={submitting}
            className="inline-flex h-11 items-center justify-center gap-2 rounded-lg border border-gray-500 bg-white px-4 text-sm font-semibold text-gray-600 transition hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50 sm:w-[180px]"
          >
            Reset
          </button>
        )}
        <div className="flex flex-col sm:items-end">
          <button
            type="button"
            onClick={onSubmit}
            disabled={submitting}
            className="inline-flex h-14 items-center justify-center rounded-lg border border-cpaGold bg-cpaGold px-10 text-lg font-bold text-white transition hover:bg-amber-500 disabled:cursor-not-allowed disabled:opacity-50 sm:min-w-[420px]"
          >
            {submitting ? "Saving..." : "Save & Publish to Website"}
          </button>
          {variant === "create" && lastSavedLabel && (
            <p className="mt-2 text-sm text-gray-500">{lastSavedLabel}</p>
          )}
        </div>
      </div>
    </div>
  );
};

const DeleteAffinityProgramPartnerModal = ({
  partner,
  deleting,
  onCancel,
  onDelete,
}: {
  partner: AffinityProgramPartnerFieldsFragment;
  deleting: boolean;
  onCancel: () => void;
  onDelete: () => void | Promise<void>;
}) => (
  <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/50 p-4">
    <div className="relative w-full max-w-[760px] rounded-[28px] border border-gray-600 bg-white px-8 pb-12 pt-16 shadow-xl sm:px-14">
      <button
        type="button"
        onClick={onCancel}
        disabled={deleting}
        className="absolute right-8 top-5 flex h-14 w-14 items-center justify-center rounded-full bg-red-500 text-3xl text-white transition hover:bg-red-600 disabled:cursor-not-allowed disabled:opacity-50"
        aria-label="Close delete confirmation"
      >
        <FontAwesomeIcon icon={faXmark} />
      </button>
      <div className="mx-auto flex h-[170px] w-[170px] items-center justify-center rounded-full bg-red-500 text-7xl text-white">
        <FontAwesomeIcon icon={faTrash} />
      </div>
      <h3 className="mt-14 text-center text-4xl font-bold leading-tight text-black">
        Delete Affinity Program Partner
      </h3>
      <p className="mx-auto mt-5 max-w-[620px] text-center text-3xl leading-snug text-black">
        Are you sure you want to delete the&nbsp; Affinity Program Partner{" "}
        {partner.companyName}?
      </p>
      <div className="mt-10 grid grid-cols-1 gap-8 sm:grid-cols-2">
        <button
          type="button"
          onClick={onDelete}
          disabled={deleting}
          className="inline-flex h-20 items-center justify-center gap-8 rounded-2xl border border-red-500 bg-red-500 px-8 text-2xl font-bold uppercase text-white shadow-lg transition hover:bg-red-600 disabled:cursor-not-allowed disabled:opacity-50"
        >
          <FontAwesomeIcon icon={faTrash} />
          {deleting ? "Deleting..." : "Delete"}
        </button>
        <button
          type="button"
          onClick={onCancel}
          disabled={deleting}
          className="inline-flex h-20 items-center justify-center rounded-2xl border border-gray-900 bg-gray-900 px-8 text-2xl font-bold uppercase text-white shadow-lg transition hover:bg-black disabled:cursor-not-allowed disabled:opacity-50"
        >
          Cancel
        </button>
      </div>
    </div>
  </div>
);

const AffinityProgramSetupTab = ({
  chamberId,
  lastSavedLabel,
  onDirtyChange,
}: {
  chamberId: string;
  lastSavedLabel: string;
  /**
   * B15 guardrail: reports draft dirtiness (non-blank create draft, or an
   * edit draft that differs from its source partner) so the parent's
   * UnsavedChangesPrompt route guard also covers this tab's unsaved work.
   */
  onDirtyChange?: (dirty: boolean) => void;
}) => {
  const [createDraft, setCreateDraft] = useState<AffinityProgramDraft>(
    createBlankDraft
  );
  const [createError, setCreateError] = useState<string | null>(null);
  const [editDraft, setEditDraft] = useState<AffinityProgramDraft | null>(null);
  const [editError, setEditError] = useState<string | null>(null);
  const [editingPartnerId, setEditingPartnerId] = useState<string | null>(null);
  const [deleteTarget, setDeleteTarget] =
    useState<AffinityProgramPartnerFieldsFragment | null>(null);
  const [lastAffinitySaveAtUtcMilli, setLastAffinitySaveAtUtcMilli] =
    useState<number | null>(null);

  const { data, loading, error, refetch } =
    useGetAllAffinityProgramPartnersForChamberQuery({
      variables: { chamberId },
      skip: !chamberId,
      fetchPolicy: "network-only",
    });

  const [createAffinityProgramPartner, { loading: creating }] =
    useCreateAffinityProgramPartnerMutation();
  const [updateAffinityProgramPartner, { loading: updating }] =
    useUpdateAffinityProgramPartnerMutation();
  const [deleteAffinityProgramPartner, { loading: deleting }] =
    useDeleteAffinityProgramPartnerMutation();

  const partners = useMemo(
    () => data?.getAllAffinityProgramPartnersForChamber || [],
    [data?.getAllAffinityProgramPartnersForChamber]
  );
  const createLastSavedLabel =
    formatSavedAt(lastAffinitySaveAtUtcMilli) || lastSavedLabel;

  // Draft dirtiness for the parent route guard: the create card is dirty as
  // soon as it holds any entered content; the edit card is dirty when its
  // draft differs from the partner it was opened from.
  const createDraftIsDirty =
    createDraft.companyName.trim() !== "" ||
    createDraft.description.trim() !== "" ||
    createDraft.logo !== "";
  const editingPartner = editingPartnerId
    ? partners.find(({ id }) => id === editingPartnerId) || null
    : null;
  const editDraftIsDirty =
    !!editDraft &&
    !!editingPartner &&
    (editDraft.companyName !== editingPartner.companyName ||
      editDraft.description !== editingPartner.description ||
      editDraft.logo !== editingPartner.logo ||
      editDraft.startDate !==
        utcMilliToDateInputValue(editingPartner.activationStartDateUtcMilli));
  const affinityDraftIsDirty = createDraftIsDirty || editDraftIsDirty;

  useEffect(() => {
    onDirtyChange?.(affinityDraftIsDirty);
  }, [affinityDraftIsDirty, onDirtyChange]);

  const handleCreate = async () => {
    const validationError = validateDraft(createDraft);

    if (validationError) {
      setCreateError(validationError);
      return;
    }

    const activationStartDateUtcMilli = dateInputValueToUtcMilli(
      createDraft.startDate
    );

    if (!activationStartDateUtcMilli) {
      setCreateError("Start Date is required.");
      return;
    }

    try {
      setCreateError(null);
      const result = await createAffinityProgramPartner({
        variables: {
          chamberId,
          activationStartDateUtcMilli,
          input: buildAffinityProgramInput({
            id: createPartnerId(),
            draft: createDraft,
          }),
        },
      });

      if (!result.data?.createAffinityProgramPartner.id) {
        throw new Error("Affinity Program Partner create returned no id.");
      }

      setCreateDraft(createBlankDraft());
      setLastAffinitySaveAtUtcMilli(Date.now());
      await refetch();
      toast.success("Affinity Program Partner saved and published.");
    } catch (mutationError: any) {
      const message =
        mutationError?.message || "Failed to save Affinity Program Partner.";
      setCreateError(message);
      toast.error(message);
    }
  };

  const handleStartEdit = (partner: AffinityProgramPartnerFieldsFragment) => {
    setEditingPartnerId(partner.id);
    setEditDraft({
      companyName: partner.companyName,
      description: partner.description,
      logo: partner.logo,
      startDate: utcMilliToDateInputValue(partner.activationStartDateUtcMilli),
      title: partner.title,
      redirectLink: partner.redirectLink,
    });
    setEditError(null);
  };

  const handleCancelEdit = () => {
    setEditingPartnerId(null);
    setEditDraft(null);
    setEditError(null);
  };

  const handleResetEdit = () => {
    const partner = partners.find(({ id }) => id === editingPartnerId);

    if (!partner) {
      return;
    }

    handleStartEdit(partner);
  };

  const handleUpdate = async () => {
    if (!editDraft || !editingPartnerId) {
      return;
    }

    const validationError = validateDraft(editDraft);

    if (validationError) {
      setEditError(validationError);
      return;
    }

    const activationStartDateUtcMilli = dateInputValueToUtcMilli(
      editDraft.startDate
    );

    if (!activationStartDateUtcMilli) {
      setEditError("Start Date is required.");
      return;
    }

    try {
      setEditError(null);
      const result = await updateAffinityProgramPartner({
        variables: {
          id: editingPartnerId,
          activationStartDateUtcMilli,
          input: buildAffinityProgramInput({
            id: editingPartnerId,
            draft: editDraft,
          }),
        },
      });

      if (!result.data?.updateAffinityProgramPartner.id) {
        throw new Error("Affinity Program Partner update returned no id.");
      }

      handleCancelEdit();
      setLastAffinitySaveAtUtcMilli(Date.now());
      await refetch();
      toast.success("Affinity Program Partner saved and published.");
    } catch (mutationError: any) {
      const message =
        mutationError?.message || "Failed to update Affinity Program Partner.";
      setEditError(message);
      toast.error(message);
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) {
      return;
    }

    try {
      const result = await deleteAffinityProgramPartner({
        variables: { id: deleteTarget.id },
      });

      if (!result.data?.deleteAffinityProgramPartner.deleted) {
        throw new Error("Affinity Program Partner delete was not confirmed.");
      }

      if (editingPartnerId === deleteTarget.id) {
        handleCancelEdit();
      }

      setDeleteTarget(null);
      setLastAffinitySaveAtUtcMilli(Date.now());
      await refetch();
      toast.success("Affinity Program Partner deleted.");
    } catch (mutationError: any) {
      const message =
        mutationError?.message || "Failed to delete Affinity Program Partner.";
      toast.error(message);
    }
  };

  return (
    <div className="space-y-8">
      <section className="rounded-lg border border-black bg-white px-6 py-8 shadow-none sm:px-9">
        <h2 className="text-2xl font-bold text-gray-950">
          Create Affinity Program Set Up
        </h2>
        <p className="mt-3 max-w-6xl text-base leading-7 text-gray-950">
          Create and manage your chamber&apos;s affinity programs and member
          savings opportunities. Programs added here will automatically appear on
          your chamber website. You can add, edit, or delete programs at any
          time and set up your Affinity Program Partners Portal within the
          Chamber Perks App{"\u2122"} to increase member engagement and
          showcase member benefits and track analytics.
        </p>
        <div className="mt-8">
          <AffinityProgramForm
            draft={createDraft}
            onChange={(draft) => {
              setCreateDraft(draft);
              setCreateError(null);
            }}
            onSubmit={handleCreate}
            submitting={creating}
            error={createError}
            variant="create"
            lastSavedLabel={createLastSavedLabel}
          />
        </div>
      </section>

      <section className="rounded-lg border border-black bg-white px-6 py-8 shadow-none sm:px-9">
        <h2 className="text-2xl font-bold text-gray-950">
          Manage Affinity Program Partners
        </h2>
        <p className="mt-2 max-w-6xl text-base leading-7 text-gray-950">
          Update descriptions and start date for each Affinity Program Partner
          below. Unpublished Affinity Program Partners will not be seen on
          your chamber website.
        </p>

        {loading ? (
          <div className="flex min-h-[220px] items-center justify-center">
            <Loader />
          </div>
        ) : error ? (
          <div className="mt-8 rounded-lg bg-red-50 px-4 py-3 text-sm text-red-700">
            <p className="font-semibold">
              Unable to load Affinity Program Partners.
            </p>
            <p className="mt-1">{error.message}</p>
            <button
              type="button"
              onClick={() => refetch()}
              className="mt-4 rounded-lg border border-red-200 bg-white px-4 py-2 text-sm font-semibold text-red-700 transition hover:bg-red-100"
            >
              Try again
            </button>
          </div>
        ) : partners.length === 0 ? (
          <div className="mt-8 rounded-lg bg-gray-50 px-5 py-4 text-sm text-gray-600">
            No Affinity Program Partners have been published yet.
          </div>
        ) : (
          <div className="mt-8 overflow-x-auto">
            <table className="min-w-[1080px] table-fixed text-left">
              <thead>
                <tr className="bg-gray-50">
                  <th className="w-[25%] px-5 py-5 text-base font-bold text-gray-950">
                    Affinity Program Partner
                  </th>
                  <th className="w-[13%] px-5 py-5 text-base font-bold text-gray-950">
                    Start Date
                  </th>
                  <th className="w-[32%] px-5 py-5 text-base font-bold text-gray-950">
                    Description
                  </th>
                  <th className="w-[16%] px-5 py-5 text-base font-bold text-gray-950">
                    Published
                  </th>
                  <th className="w-[14%] px-5 py-5 text-right text-base font-bold text-gray-950">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody>
                {partners.map((partner, index) => {
                  const published = isPublishedPartner(partner);
                  const isEditing = editingPartnerId === partner.id;

                  return (
                    <React.Fragment key={partner.id}>
                      <tr className="align-top">
                        <td className="px-5 py-6 text-base font-semibold text-gray-950">
                          <span className="mr-2 text-xs text-gray-950">
                            <FontAwesomeIcon icon={faGripVertical} />
                          </span>
                          {partner.companyName}
                        </td>
                        <td className="px-5 py-6 text-base text-gray-950">
                          {formatDate(partner.activationStartDateUtcMilli)}
                        </td>
                        <td className="px-5 py-6 text-base leading-7 text-gray-950">
                          {truncateDescription(partner.description)}
                        </td>
                        <td className="px-5 py-6">
                          <span
                            className={`inline-flex items-center gap-3 text-base font-semibold ${
                              published ? "text-green-700" : "text-red-500"
                            }`}
                          >
                            <span
                              className={`h-2.5 w-2.5 rounded-full ${
                                published ? "bg-green-500" : "bg-red-500"
                              }`}
                            />
                            {published ? "Published" : "Not Published"}
                          </span>
                        </td>
                        <td className="px-5 py-6">
                          <div className="flex justify-end gap-3">
                            {isEditing ? (
                              <button
                                type="button"
                                onClick={handleCancelEdit}
                                disabled={updating}
                                className="inline-flex h-8 items-center justify-center rounded-md border border-black bg-black px-4 text-sm font-bold text-white transition hover:bg-gray-900 disabled:cursor-not-allowed disabled:opacity-50"
                              >
                                Cancel
                              </button>
                            ) : (
                              <>
                                <button
                                  type="button"
                                  onClick={() => handleStartEdit(partner)}
                                  disabled={updating || deleting}
                                  className="inline-flex h-8 items-center justify-center gap-2 rounded-md border border-cpaGold bg-cpaGold px-4 text-sm font-bold text-white transition hover:bg-amber-500 disabled:cursor-not-allowed disabled:opacity-50"
                                >
                                  <FontAwesomeIcon icon={faPenToSquare} />
                                  Edit
                                </button>
                                <button
                                  type="button"
                                  onClick={() => setDeleteTarget(partner)}
                                  disabled={updating || deleting}
                                  className="inline-flex h-8 items-center justify-center gap-2 rounded-md border border-red-500 bg-red-500 px-4 text-sm font-bold text-white transition hover:bg-red-600 disabled:cursor-not-allowed disabled:opacity-50"
                                >
                                  <FontAwesomeIcon icon={faTrash} />
                                  Delete
                                </button>
                              </>
                            )}
                          </div>
                        </td>
                      </tr>
                      {isEditing && editDraft && (
                        <tr>
                          <td colSpan={5} className="px-5 pb-8 pt-2">
                            <AffinityProgramForm
                              draft={editDraft}
                              onChange={(draft) => {
                                setEditDraft(draft);
                                setEditError(null);
                              }}
                              onSubmit={handleUpdate}
                              onReset={handleResetEdit}
                              submitting={updating}
                              error={editError}
                              variant="edit"
                            />
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </section>

      {deleteTarget && (
        <DeleteAffinityProgramPartnerModal
          partner={deleteTarget}
          deleting={deleting}
          onCancel={() => setDeleteTarget(null)}
          onDelete={handleDelete}
        />
      )}
    </div>
  );
};

export default AffinityProgramSetupTab;
